import type { OpUnitType } from "dayjs";
import dayjs from "dayjs";

import type { HeatMapData } from "./heatmap.shared";

type AggregationUnit = OpUnitType;

type DurationPreset = "7d" | "28d" | "1m" | "2m" | "3m" | "6m" | "1y" | "3y";

interface IPresetConfig {
    unit: AggregationUnit;
    labelEvery: number;
}

const PRESET_CONFIG: Record<DurationPreset, IPresetConfig> = {
    "7d": { unit: "day", labelEvery: 1 },
    "28d": { unit: "day", labelEvery: 3 },
    "1m": { unit: "day", labelEvery: 3 },
    "2m": { unit: "week", labelEvery: 1 },
    "3m": { unit: "week", labelEvery: 2 },
    "6m": { unit: "month", labelEvery: 1 },
    "1y": { unit: "month", labelEvery: 1 },
    "3y": { unit: "month", labelEvery: 3 },
};

export const MAX_COLUMNS = 37;
const TARGET_VISIBLE_LABELS = 12;

const deriveLabelEvery = (colCount: number): number => Math.max(1, Math.ceil(colCount / TARGET_VISIBLE_LABELS));

// Parse and bucket in UTC: the backend sends/expects UTC date-only values, so a local-timezone read
// (e.g. CET) would shift a date-only input back across midnight into the previous day, landing
// week/month boundaries — and the column label — a day early.
function getBucketStart(dateStr: string, unit: AggregationUnit): dayjs.Dayjs {
    const date = dayjs.utc(dateStr);

    if (unit === "day") {
        return date.startOf("day");
    }

    if (unit === "week") {
        return date.startOf("isoWeek");
    }

    return date.startOf("month");
}

// A leading week/month bucket starts on its ISO-week Monday or month-1st, which can fall before the
// picked range start (e.g. a Nov 1 range sits in the ISO week beginning Oct 27). The cell only holds
// in-range data, so clamp the start used for its label to the range start — otherwise the axis header
// reads as out-of-range data. Applied identically to column generation and event bucketing so the
// label stays a stable cell key.
function getLabelStart(bucketStart: dayjs.Dayjs, rangeFrom?: dayjs.Dayjs): dayjs.Dayjs {
    return rangeFrom && bucketStart.isBefore(rangeFrom) ? rangeFrom : bucketStart;
}

function countBuckets(dateFrom: string, dateTo: string, unit: AggregationUnit): number {
    const end = getBucketStart(dateTo, unit);
    let cursor = getBucketStart(dateFrom, unit);
    let count = 0;
    while (cursor.valueOf() <= end.valueOf()) {
        count += 1;
        cursor = cursor.add(1, unit);
    }
    return count;
}

function pickConfigDynamic(totalDays: number, dateFrom?: string, dateTo?: string): IPresetConfig {
    const hasRange = Boolean(dateFrom && dateTo);
    const dayCount = hasRange ? countBuckets(dateFrom!, dateTo!, "day") : totalDays;
    if (dayCount <= MAX_COLUMNS) {
        return { unit: "day", labelEvery: deriveLabelEvery(dayCount) };
    }

    // Approximate week count: ceil(days/7) + 1 accounts for partial weeks at start/end.
    const weekCount = hasRange ? countBuckets(dateFrom!, dateTo!, "week") : Math.ceil(totalDays / 7) + 1;
    if (weekCount <= MAX_COLUMNS) {
        return { unit: "week", labelEvery: deriveLabelEvery(weekCount) };
    }

    const monthCount = hasRange ? countBuckets(dateFrom!, dateTo!, "month") : Math.ceil(totalDays / 30) + 1;
    return { unit: "month", labelEvery: deriveLabelEvery(monthCount) };
}

function formatBucketLabel(start: dayjs.Dayjs, unit: AggregationUnit): string {
    if (unit === "day") {
        return start.format("D MMM");
    }

    if (unit === "week") {
        return start.format("MMM D");
    }

    return start.format("MMM YYYY");
}

function buildRangeColumns(
    dateFrom: string,
    dateTo: string,
    unit: AggregationUnit,
): Array<{ colKey: string; timestamp: number }> {
    const result: Array<{ colKey: string; timestamp: number }> = [];
    const rangeFrom = dayjs.utc(dateFrom);
    const end = getBucketStart(dateTo, unit);
    let cursor = getBucketStart(dateFrom, unit);

    while (cursor.valueOf() <= end.valueOf()) {
        const labelStart = getLabelStart(cursor, rangeFrom);
        result.push({ colKey: formatBucketLabel(labelStart, unit), timestamp: labelStart.valueOf() });
        cursor = cursor.add(1, unit);
    }

    return result;
}

interface IBucket<TEvent> {
    events: TEvent[];
    dateFrom: string;
    dateTo: string;
    unitRangeConfig: AggregationUnit;
}

interface IBuildFromEventsConfig<TEvent> {
    preset?: DurationPreset;
    totalDays?: number;
    dateFrom?: string;
    dateTo?: string;
    getDate: (event: TEvent) => string;
    getValue?: (events: TEvent[]) => number;
}

interface IHeatMapEventsResult<TEvent> {
    data: HeatMapData;
    lookup: (rowKey: string, colKey: string) => IBucket<TEvent> | undefined;
    labelEvery: number;
}

function buildHeatMapFromEvents<TEvent>(
    eventsByRow: Record<string, TEvent[]>,
    config: IBuildFromEventsConfig<TEvent>,
): IHeatMapEventsResult<TEvent> {
    const presetConfig = config.preset
        ? PRESET_CONFIG[config.preset]
        : config.totalDays
          ? pickConfigDynamic(config.totalDays, config.dateFrom, config.dateTo)
          : PRESET_CONFIG["3m"];

    const getValue = config.getValue ?? ((events: TEvent[]) => events.length);
    const lookupMap = new Map<string, IBucket<TEvent>>();
    const columnOrder = new Map<string, number>();
    const rowsBuckets: Record<string, Map<string, TEvent[]>> = {};
    const dateCache = new Map<string, { colKey: string; timestamp: number }>();
    const rangeFrom = config.dateFrom ? dayjs.utc(config.dateFrom) : undefined;

    if (config.dateFrom && config.dateTo) {
        for (const { colKey, timestamp } of buildRangeColumns(config.dateFrom, config.dateTo, presetConfig.unit)) {
            columnOrder.set(colKey, timestamp);
        }
    }

    for (const [rowKey, events] of Object.entries(eventsByRow)) {
        const bucketEvents = new Map<string, TEvent[]>();

        for (const event of events) {
            const dateStr = config.getDate(event);
            let cached = dateCache.get(dateStr);

            if (!cached) {
                const bucketStart = getLabelStart(getBucketStart(dateStr, presetConfig.unit), rangeFrom);
                cached = {
                    colKey: formatBucketLabel(bucketStart, presetConfig.unit),
                    timestamp: bucketStart.valueOf(),
                };
                dateCache.set(dateStr, cached);
            }

            const { colKey, timestamp } = cached;

            if (!columnOrder.has(colKey)) {
                columnOrder.set(colKey, timestamp);
            }

            let bucket = bucketEvents.get(colKey);

            if (!bucket) {
                bucket = [];
                bucketEvents.set(colKey, bucket);
            }

            bucket.push(event);
        }

        rowsBuckets[rowKey] = bucketEvents;
    }

    const columns = Array.from(columnOrder.entries())
        .sort(([, a], [, b]) => a - b)
        .map(([colKey]) => colKey);

    const data: HeatMapData = {};

    for (const [rowKey, bucketEvents] of Object.entries(rowsBuckets)) {
        const rowData: Record<string, number> = {};

        for (const colKey of columns) {
            const bucketEvts = bucketEvents.get(colKey);
            rowData[colKey] = bucketEvts ? getValue(bucketEvts) : 0;

            if (!bucketEvts) {
                continue;
            }

            const dates = bucketEvts.map((e) => config.getDate(e)).sort();

            lookupMap.set(`${rowKey}\0${colKey}`, {
                events: bucketEvts,
                dateFrom: dates[0],
                dateTo: dates[dates.length - 1],
                unitRangeConfig: presetConfig.unit,
            });
        }

        data[rowKey] = rowData;
    }

    const lookup = (rowKey: string, colKey: string) => lookupMap.get(`${rowKey}\0${colKey}`);

    return { data, lookup, labelEvery: presetConfig.labelEvery };
}

export { buildHeatMapFromEvents };
export type { DurationPreset, IBucket, IBuildFromEventsConfig, IHeatMapEventsResult };
